import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

const PROGRAMS = [
  {
    title: "Stray Dog Rescue",
    desc: "Cold-weather rescues, mobile veterinary clinics, sterilization programs, and forever-home placement across 14 countries. We've placed over 9,200 dogs in permanent homes since 2018.",
    img: "https://images.unsplash.com/photo-1700922180830-009061498903?crop=entropy&cs=srgb&fm=jpg&w=1600",
    impact: ["9,200+ rehomed", "47 clinics funded", "12 cold-weather teams"],
  },
  {
    title: "Wildlife Protection",
    desc: "Funding anti-poaching units, habitat preservation, and indigenous ranger programs. From Kenyan savannahs and Indonesian rainforests to Romanian bear sanctuaries.",
    img: "https://images.unsplash.com/photo-1517486430290-35657bdcef51?crop=entropy&cs=srgb&fm=jpg&w=1600",
    impact: ["38 elephants saved (Mara)", "11 ranger programs", "230 km² protected"],
  },
  {
    title: "Community Aid — The Less Privileged",
    desc: "Animal welfare is inseparable from human poverty. We partner with soup kitchens, clinics, and shelters to support the communities living alongside the animals we protect.",
    img: "https://images.pexels.com/photos/6646867/pexels-photo-6646867.jpeg?auto=compress&cs=tinysrgb&w=1600",
    impact: ["1,200 families served", "14 tons of supplies/quarter", "8 partner shelters"],
  },
  {
    title: "Sanctuary Network",
    desc: "23 sanctuaries housing animals too injured, elderly, or traumatized to be released. Lifelong care — never euthanasia for treatable conditions.",
    img: "https://images.pexels.com/photos/160709/raccoon-animal-animal-world-wildlife-photography-160709.jpeg?auto=compress&cs=tinysrgb&w=1600",
    impact: ["23 sanctuaries", "1,800 permanent residents", "6 countries"],
  },
];

export default function Causes() {
  return (
    <div data-testid="causes-page" className="bg-[#0A0A0C] text-white">
      <section className="pt-20 pb-16 max-w-7xl mx-auto px-6 lg:px-10">
        <div className="text-xs tracking-[0.3em] uppercase text-[#E85D04] mb-6">Our Programs</div>
        <h1 className="font-serif-display text-6xl sm:text-7xl lg:text-8xl leading-[0.92] tracking-tighter max-w-4xl">
          Where we work. <br /><em className="text-[#E85D04] not-italic">Why it matters.</em>
        </h1>
      </section>

      <section className="border-t border-white/10">
        {PROGRAMS.map((p, i) => (
          <article
            key={p.title}
            data-testid={`program-${i}`}
            className={`border-b border-white/10 py-20 ${i % 2 === 1 ? "bg-[#121215]" : ""}`}
          >
            <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
              <div className={`lg:col-span-7 ${i % 2 === 1 ? "lg:order-2" : ""}`}>
                <div className="relative aspect-[5/3] overflow-hidden border border-white/10 group">
                  <div
                    className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-105"
                    style={{ backgroundImage: `url(${p.img})` }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0C]/60 to-transparent" />
                </div>
              </div>
              <div className={`lg:col-span-5 ${i % 2 === 1 ? "lg:order-1" : ""}`}>
                <div className="text-xs tracking-[0.3em] uppercase text-white/40 mb-4">
                  Program {String(i + 1).padStart(2, "0")}
                </div>
                <h2 className="font-serif-display text-4xl lg:text-5xl leading-[0.95] tracking-tight">
                  {p.title}
                </h2>
                <p className="mt-6 text-white/70 text-lg leading-relaxed">{p.desc}</p>
                <ul className="mt-8 space-y-3">
                  {p.impact.map((x) => (
                    <li key={x} className="flex items-center gap-3 text-sm">
                      <span className="inline-block w-6 h-px bg-[#E85D04]" />
                      <span className="text-white/80 tracking-wide">{x}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </article>
        ))}
      </section>

      <section className="py-32 max-w-7xl mx-auto px-6 lg:px-10 text-center">
        <h2 className="font-serif-display text-5xl lg:text-6xl leading-[0.95] tracking-tight">
          Support the work. <em className="not-italic text-[#E85D04]">Or join it.</em>
        </h2>
        <div className="mt-10 flex flex-wrap gap-4 justify-center">
          <Link to="/donate" data-testid="causes-donate-btn" className="bg-[#E85D04] hover:bg-[#DC2F02] text-white px-8 py-4 text-xs tracking-[0.22em] uppercase inline-flex items-center gap-3 transition-colors">
            Donate <ArrowRight size={14} />
          </Link>
          <Link to="/volunteer" data-testid="causes-volunteer-btn" className="border border-white/25 hover:border-white text-white px-8 py-4 text-xs tracking-[0.22em] uppercase">
            Volunteer
          </Link>
        </div>
      </section>
    </div>
  );
}
